/**
 *
 * AppWithRouterAccess.js
 *
 * This component is the skeleton around the actual pages, and should only
 * contain code that should be seen on all pages. (e.g. navigation bar)
 *
 */
import { toRelativeUrl } from '@okta/okta-auth-js';
import { LoginCallback, Security, useOktaAuth } from '@okta/okta-react';
import OktaLogin from 'containers/Launch/Login';
import React from 'react';
import PropTypes from 'prop-types';
import { Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import TermsAndConditionsPage from 'containers/Launch/Login/Legal/TermsAndConditions';
import NotFoundPage from 'containers/NotFoundPage/Loadable';
import PowerBIPage from 'containers/PowerBIPage/Loadable';
import SitesDashboard from 'containers/SitesPage/Loadable';
import ReportingPage from 'containers/ReportingPage/Loadable';
import ResourcesPage from 'containers/ResourcesPage/Loadable';
import SettingsPage from 'containers/SettingsPage/Loadable';
import DeviceDetailsPage from 'containers/SitesPage/DeviceDetails/Loadable';
import DocumentManager from 'containers/DocumentManager/Loadable';
import SitesDetailsPage from 'containers/SitesPage/SiteDetails/Loadable';
import EditNotifications from 'containers/EditNotifications/Loadable';
import RolePermissions from 'containers/RolePermissions/Loadable';
import { oktaAuthClient, oktaSignInConfig } from 'system/auth';
import { initializeHttpInterceptors } from 'system/httpHelper';
import ChartBuilderPage from 'containers/ChartBuilder';

/**
 * RequiredAuth
 * A wrapper for routes that require authentication.
 * If the user is not authenticated, it redirects them to the login page.
 */
function RequiredAuth({ children }) {
  const { authState } = useOktaAuth();

  if (!authState) {
    return null; // Or a loading spinner
  }

  return authState.isAuthenticated ? children : <Navigate to="/login" />;
}

RequiredAuth.propTypes = {
  children: PropTypes.element.isRequired,
};

/**
 * AppWithRouterAccess Method is executed to load all the components in place
 * and run the application.
 * It also includes the routes declaration.
 */
function AppWithRouterAccess() {
  const oktaAuth = oktaAuthClient;
  const navigate = useNavigate();

  const restoreOriginalUri = async (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
  };

  initializeHttpInterceptors();

  return (
    <Security oktaAuth={oktaAuth} restoreOriginalUri={restoreOriginalUri}>
      <Routes>
        <Route
          path="/"
          element={
            <RequiredAuth>
              <SitesDashboard />
            </RequiredAuth>
          }
        />
        <Route
          path="/reporting"
          element={
            <RequiredAuth>
              <ReportingPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/dataVisualization"
          element={
            <RequiredAuth>
              <PowerBIPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/settings"
          element={
            <RequiredAuth>
              <SettingsPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/resources"
          element={
            <RequiredAuth>
              <ResourcesPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/editNotifications"
          element={
            <RequiredAuth>
              <EditNotifications />
            </RequiredAuth>
          }
        />
        <Route
          path="/documentManager"
          element={
            <RequiredAuth>
              <DocumentManager />
            </RequiredAuth>
          }
        />
        <Route
          path="/sites/:siteId/details/:tabName?"
          element={
            <RequiredAuth>
              <SitesDetailsPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/sites/:siteId/group/:deviceGroupId/device/:deviceId/details"
          element={
            <RequiredAuth>
              <DeviceDetailsPage />
            </RequiredAuth>
          }
        />
        <Route
          path="/editPermissions"
          element={
            <RequiredAuth>
              <RolePermissions />
            </RequiredAuth>
          }
        />

        {/* Public routes */}
        <Route path="/terms" element={<TermsAndConditionsPage isConsentFlow={false} />} />
        <Route path="/login" element={<OktaLogin config={oktaSignInConfig} />} />
        <Route path="/login/callback" element={<LoginCallback />} />

        {/* New Chart Builder route */}
        <Route
          path="/chart-builder"
          element={
            <RequiredAuth>
              <ChartBuilderPage />
            </RequiredAuth>
          }
        />

        {/* Catch-all */}
        <Route
          path="*"
          element={
            <RequiredAuth>
              <NotFoundPage />
            </RequiredAuth>
          }
        />
      </Routes>
    </Security>
  );
}

export default AppWithRouterAccess;
